const uid = (prefix = 'scene') => `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const cleanBrandValue = (value) => (typeof value === 'string' ? value.trim() : '')

// Company records span legacy web and current mobile shapes. Prefer the logo
// used by the mobile splash/reel experience, then the activity-feed logo, and
// finally the flat web/signup fields. Never substitute Happily's product mark
// for a company's identity.
export const companyBrandLogo = (company = {}) => (
  cleanBrandValue(company.splash_screen && company.splash_screen.splash_logo)
  || cleanBrandValue(company.logo && company.logo.feed_logo)
  || cleanBrandValue(company.splashscreen_logo)
  || cleanBrandValue(company.brand_logo_url)
)

// Splash logos are commonly exported on a wide transparent canvas, so a
// compact `contain` frame makes the visible mark far too small. Crop those
// standardized splash assets; preserve full feed/signup wordmarks.
export const companyBrandLogoFit = (company = {}) => (
  cleanBrandValue(company.splash_screen && company.splash_screen.splash_logo)
  || cleanBrandValue(company.splashscreen_logo)
    ? 'cover'
    : 'contain'
)

export const companyBrandName = (company = {}) => (
  cleanBrandValue(company.name)
  || cleanBrandValue(company.company_name)
  || 'Company'
)

export const companyBrandInitial = (company = {}) => (
  Array.from(companyBrandName(company))[0].toLocaleUpperCase()
)

export const REEL_SCENE_STYLES = [
  { key: 'brand', backgroundColor: '#6c30cc', panelColor: 'rgba(15,10,28,.76)', textColor: '#ffffff', accentColor: '#d7c2f5' },
  { key: 'midnight', backgroundColor: '#20143d', panelColor: 'rgba(108,48,204,.88)', textColor: '#ffffff', accentColor: '#59d6e5' },
  { key: 'spotlight', backgroundColor: '#522698', panelColor: 'rgba(255,255,255,.94)', textColor: '#21162f', accentColor: '#f4c95d' },
  { key: 'fresh', backgroundColor: '#e9f8fa', panelColor: 'rgba(255,255,255,.94)', textColor: '#18373b', accentColor: '#32c2d5' },
  { key: 'paper', backgroundColor: '#f5f2fa', panelColor: 'rgba(255,255,255,.96)', textColor: '#261a35', accentColor: '#6c30cc' },
]

// Each preset intentionally combines a restrained grade with one subtle camera
// move. The stable key is persisted so the renderer can reproduce the same
// treatment without relying on browser-only CSS.
export const REEL_EFFECT_PRESETS = [
  { key: 'natural', filter: 'none', overlayColor: 'transparent', motion: 'none' },
  { key: 'studio', filter: 'brightness(1.03) contrast(1.06) saturate(1.08)', overlayColor: 'rgba(108,48,204,.025)', motion: 'slow-zoom' },
  { key: 'cinematic', filter: 'brightness(.96) contrast(1.13) saturate(.88)', overlayColor: 'rgba(24,16,42,.10)', motion: 'push-in' },
  { key: 'sunlit', filter: 'brightness(1.04) contrast(1.03) saturate(1.10) sepia(.10)', overlayColor: 'rgba(244,170,92,.06)', motion: 'drift' },
  { key: 'vibrant', filter: 'brightness(1.02) contrast(1.08) saturate(1.22)', overlayColor: 'rgba(50,194,213,.025)', motion: 'lift' },
  { key: 'mono', filter: 'grayscale(1) brightness(1.02) contrast(1.12)', overlayColor: 'rgba(32,20,61,.05)', motion: 'pull-back' },
]

export const getEffectPreset = (key) => REEL_EFFECT_PRESETS.find((item) => item.key === key) || REEL_EFFECT_PRESETS[0]

export const REEL_TEXT_EFFECT_PRESETS = [
  { key: 'none', motion: 'none' },
  { key: 'soft-rise', motion: 'soft-rise' },
  { key: 'side-reveal', motion: 'side-reveal' },
  { key: 'focus', motion: 'focus' },
  { key: 'stagger', motion: 'stagger' },
  { key: 'highlight', motion: 'highlight' },
]

export const getTextEffectPreset = (key) => REEL_TEXT_EFFECT_PRESETS.find((item) => item.key === key) || REEL_TEXT_EFFECT_PRESETS[0]

// Curated soundtrack moods. Only the stable key is persisted — the render
// service resolves it to licensed audio, same as the effect presets.
// 'custom' means the author uploaded their own file (kept in audio.track).
export const REEL_AUDIO_TRACKS = [
  { key: 'none' },
  { key: 'uplift' },
  { key: 'calm' },
  { key: 'energy' },
  { key: 'warm' },
  { key: 'focus' },
]

export const getAudioTrackKey = (key, hasCustomTrack) => {
  if (key === 'custom' || (!key && hasCustomTrack)) return hasCustomTrack ? 'custom' : 'none'
  return REEL_AUDIO_TRACKS.some((item) => item.key === key) ? key : 'none'
}

const assetKey = (value) => String((value && (value.id || value.assetId || value.asset_id)) || '')
const sceneAssetKey = (value) => String((value && (value.assetId || value.asset_id)) || '')
const hasAssetRef = (value) => Boolean(value && (value.assetId || value.asset_id))
const assetPreviewUrl = (value) => value && (value.url || value.download_url || value.downloadUrl || value.media_url || value.mediaUrl)
const assetLookup = (assets = []) => {
  const lookup = {}
  ;(Array.isArray(assets) ? assets : Object.values(assets || {})).filter(Boolean).forEach((asset) => {
    const key = assetKey(asset)
    if (key) lookup[key] = asset
  })
  return lookup
}

const scene = (overrides = {}) => {
  const style = REEL_SCENE_STYLES.find((item) => item.key === (overrides.styleKey || 'brand')) || REEL_SCENE_STYLES[0]
  return {
    id: uid(),
    type: 'text',
    title: '',
    supportingText: '',
    durationSec: 5,
    fit: 'contain',
    focalPoint: { x: 50, y: 50 },
    transition: 'crossfade',
    mediaUrl: '',
    mediaName: '',
    mediaType: '',
    assetId: '',
    object_path: '',
    trimStartSec: 0,
    trimEndSec: null,
    styleKey: style.key,
    layout: 'lower-third',
    backgroundColor: style.backgroundColor,
    panelColor: style.panelColor,
    textColor: style.textColor,
    accentColor: style.accentColor,
    ...overrides,
    effectKey: getEffectPreset(overrides.effectKey || 'natural').key,
    textEffectKey: getTextEffectPreset(overrides.textEffectKey || 'none').key,
  }
}

export const REEL_TEMPLATES = [
  {
    key: 'company_update',
    label: 'Company update',
    icon: 'el-icon-data-analysis',
    scenes: [
      scene({ styleKey: 'brand', layout: 'statement', textEffectKey: 'highlight', title: 'Company update', supportingText: 'The three things everyone should know this week.', durationSec: 5 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'soft-rise', title: 'The headline', supportingText: 'Replace this with the most important change in one sentence.', durationSec: 6 }),
      scene({ type: 'image', styleKey: 'fresh', layout: 'lower-third', effectKey: 'studio', textEffectKey: 'stagger', title: 'Why it matters', supportingText: 'Add a useful photo, chart, or screenshot and explain the impact.', durationSec: 7 }),
      scene({ styleKey: 'midnight', layout: 'center', textEffectKey: 'side-reveal', title: 'What happens next', supportingText: 'Give people one clear next step, owner, or date.', durationSec: 6 }),
      scene({ styleKey: 'brand', layout: 'statement', textEffectKey: 'soft-rise', title: 'Questions?', supportingText: 'Tell people where to ask or learn more.', durationSec: 5 }),
    ],
  },
  {
    key: 'welcome_team',
    label: 'Welcome teammates',
    icon: 'el-icon-user',
    scenes: [
      scene({ styleKey: 'fresh', layout: 'statement', textEffectKey: 'soft-rise', title: 'Welcome to the team', supportingText: "We're so glad you're here.", durationSec: 5 }),
      scene({ type: 'image', styleKey: 'brand', layout: 'lower-third', effectKey: 'sunlit', textEffectKey: 'highlight', title: 'Meet [name]', supportingText: '[Role] · [Team] · Based in [location]', durationSec: 7 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'stagger', title: 'A little about them', supportingText: 'Add one work passion and one human detail people can connect with.', durationSec: 7 }),
      scene({ type: 'video', styleKey: 'midnight', layout: 'lower-third', effectKey: 'studio', textEffectKey: 'side-reveal', title: 'Say hello', supportingText: 'Add a short welcome clip from their manager or teammates.', durationSec: 12 }),
      scene({ styleKey: 'spotlight', layout: 'center', textEffectKey: 'focus', title: 'Welcome, [name]!', supportingText: 'Invite everyone to introduce themselves this week.', durationSec: 5 }),
    ],
  },
  {
    key: 'recognition',
    label: 'Recognition or milestone',
    icon: 'el-icon-medal',
    scenes: [
      scene({ styleKey: 'spotlight', layout: 'statement', textEffectKey: 'focus', title: 'A moment worth celebrating', supportingText: 'Name the person, team, or milestone.', durationSec: 5 }),
      scene({ type: 'image', styleKey: 'brand', layout: 'lower-third', effectKey: 'vibrant', textEffectKey: 'highlight', title: 'This is what great looks like', supportingText: 'Add the moment and the behavior that made it possible.', durationSec: 7 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'stagger', title: 'The impact', supportingText: 'Share a customer result, team outcome, or meaningful number.', durationSec: 6 }),
      scene({ type: 'video', styleKey: 'midnight', layout: 'lower-third', effectKey: 'studio', textEffectKey: 'side-reveal', title: 'In their own words', supportingText: 'Optional: add a quick thank-you or reaction clip.', durationSec: 10 }),
      scene({ styleKey: 'spotlight', layout: 'center', textEffectKey: 'focus', title: 'Thank you, [name or team]', supportingText: 'End with a genuine, specific note of appreciation.', durationSec: 5 }),
    ],
  },
  {
    key: 'leadership_message',
    label: 'Leadership message',
    icon: 'el-icon-microphone',
    scenes: [
      scene({ styleKey: 'midnight', layout: 'statement', textEffectKey: 'soft-rise', title: 'A message from [leader]', supportingText: 'Set the context in one clear sentence.', durationSec: 5 }),
      scene({ type: 'video', styleKey: 'brand', layout: 'lower-third', effectKey: 'cinematic', textEffectKey: 'soft-rise', title: 'What I want you to know', supportingText: 'Add the main leadership message in a 15–30 second clip.', durationSec: 20 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'stagger', title: 'Three takeaways', supportingText: 'Summarize the message so it works without sound.', durationSec: 8 }),
      scene({ styleKey: 'fresh', layout: 'center', textEffectKey: 'side-reveal', title: 'What this means for us', supportingText: 'Connect the message to one practical team action.', durationSec: 6 }),
    ],
  },
  {
    key: 'event',
    label: 'Event reminder or recap',
    icon: 'el-icon-date',
    scenes: [
      scene({ styleKey: 'brand', layout: 'statement', textEffectKey: 'focus', title: 'Save the date', supportingText: '[Event name] · [Day and date]', durationSec: 5 }),
      scene({ type: 'image', styleKey: 'fresh', layout: 'lower-third', effectKey: 'vibrant', textEffectKey: 'soft-rise', title: 'Why you’ll want to be there', supportingText: 'Add an event image and one compelling reason to join.', durationSec: 7 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'stagger', title: 'What to expect', supportingText: 'Add the format, speakers, agenda, or three highlights.', durationSec: 7 }),
      scene({ styleKey: 'midnight', layout: 'center', textEffectKey: 'highlight', title: '[Time] · [Place]', supportingText: 'Include the room, link, timezone, or arrival instructions.', durationSec: 6 }),
      scene({ styleKey: 'spotlight', layout: 'statement', textEffectKey: 'focus', title: 'Add it to your calendar', supportingText: 'End with the one action people should take.', durationSec: 5 }),
    ],
  },
  {
    key: 'policy',
    label: 'Policy or deadline',
    icon: 'el-icon-document-checked',
    scenes: [
      scene({ styleKey: 'midnight', layout: 'statement', textEffectKey: 'highlight', title: 'Important update', supportingText: 'Name the policy, process, or deadline.', durationSec: 5 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'soft-rise', title: 'What is changing', supportingText: 'Explain the change in plain language—without policy jargon.', durationSec: 7 }),
      scene({ styleKey: 'fresh', layout: 'center', textEffectKey: 'side-reveal', title: 'Who this affects', supportingText: 'Be explicit about teams, locations, or employment groups.', durationSec: 6 }),
      scene({ styleKey: 'brand', layout: 'lower-third', textEffectKey: 'focus', title: 'Action required by [date]', supportingText: 'Give one clear action and where to complete it.', durationSec: 7 }),
      scene({ styleKey: 'paper', layout: 'editorial', textEffectKey: 'soft-rise', title: 'Need help?', supportingText: 'Add the owner, channel, or source of truth.', durationSec: 5 }),
    ],
  },
  { key: 'blank', label: 'Blank reel', icon: 'el-icon-plus', scenes: [scene({ textEffectKey: 'soft-rise', title: 'Your story starts here' })] },
]

export const getTemplate = (key) => REEL_TEMPLATES.find((item) => item.key === key) || REEL_TEMPLATES[0]

export const seedTemplate = (key) => getTemplate(key).scenes.map((item) => scene({ ...item, id: uid() }))

export const createBlankReel = (templateKey = 'company_update') => ({
  id: '',
  version: 0,
  source: 'builder',
  templateKey,
  title: '',
  caption: '',
  tags: [],
  audience: [['everyone']],
  huddles: [],
  audienceKeys: ['all'],
  publicationStatus: 'draft',
  renderStatus: 'not_requested',
  scheduledAt: null,
  publishTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  scenes: seedTemplate(templateKey),
  uploadedVideo: null,
  uploadFit: 'contain',
  uploadFocalPoint: { x: 50, y: 50 },
  poster: { mode: 'auto', timeSec: 0 },
  uploadEffectKey: 'natural',
  showBranding: true,
  assets: [],
  subtitles: null,
  audio: { clipVolume: 60, trackVolume: 30, track: null, trackKey: 'none' },
  analytics: { views: 0, completions: 0, reactions: 0 },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
})

export const duplicateScene = (source) => scene({
  ...source,
  id: uid(),
  focalPoint: { ...(source.focalPoint || { x: 50, y: 50 }) },
})

export const applySceneStyle = (source, styleKey) => {
  const style = REEL_SCENE_STYLES.find((item) => item.key === styleKey) || REEL_SCENE_STYLES[0]
  return Object.assign(source, {
    styleKey: style.key,
    backgroundColor: style.backgroundColor,
    panelColor: style.panelColor,
    textColor: style.textColor,
    accentColor: style.accentColor,
  })
}

export const applySceneEffect = (source, effectKey) => Object.assign(source, {
  effectKey: getEffectPreset(effectKey).key,
})

export const applySceneTextEffect = (source, textEffectKey) => Object.assign(source, {
  textEffectKey: getTextEffectPreset(textEffectKey).key,
})

export const totalDuration = (reel) => {
  if (!reel) return 0
  if (reel.source === 'upload') return Number((reel.uploadedVideo || {}).durationSec) || 0
  return (reel.scenes || []).reduce((sum, item) => sum + (Number(item.durationSec) || 0), 0)
}

export const sceneNeedsMedia = (item) => Boolean(item && item.type !== 'text' && !item.mediaUrl && !item.assetId)

// Errors are returned as i18n keys (happilyVillage.reels.errors.*) so the
// Studio can render them in the viewer's language.
export const validateReel = (reel) => {
  const errors = []
  if (!String(reel.title || '').trim()) errors.push({ field: 'title', key: 'title_required' })
  if (!String(reel.caption || '').trim()) errors.push({ field: 'caption', key: 'caption_required' })
  if (String(reel.title || '').length > 80) errors.push({ field: 'title', key: 'title_too_long' })
  if (String(reel.caption || '').length > 280) errors.push({ field: 'caption', key: 'caption_too_long' })
  if (reel.source === 'upload' && !(reel.uploadedVideo && reel.uploadedVideo.url)) {
    errors.push({ field: 'uploadedVideo', key: 'upload_required' })
  }
  if (reel.source === 'builder') {
    if (!(reel.scenes || []).length) errors.push({ field: 'scenes', key: 'scenes_required' })
    if ((reel.scenes || []).length > 10) errors.push({ field: 'scenes', key: 'too_many_scenes' })
    if (totalDuration(reel) > 90) errors.push({ field: 'duration', key: 'too_long' })
    const missing = (reel.scenes || []).reduce((list, item, index) => (sceneNeedsMedia(item) ? [...list, index + 1] : list), [])
    if (missing.length) errors.push({ field: 'scenes', key: 'scene_media_required', params: { scenes: missing.join(', ') } })
  }
  return errors
}

const sceneSignature = (items) => JSON.stringify((items || []).map(({ id, file, ...rest }) => rest))

export const scenesMatchTemplate = (scenes, templateKey) => sceneSignature(scenes) === sceneSignature(getTemplate(templateKey).scenes)

export const audiencePayload = (values = [], options = []) => {
  const flat = (values || []).map((value) => Array.isArray(value) ? value[value.length - 1] : value).filter(Boolean)
  if (!flat.length || flat.includes('everyone') || flat.includes('all')) return { audienceKeys: ['all'], huddles: [] }

  const lookup = {}
  ;(options || []).forEach((item) => { lookup[item.value] = item })
  const audienceKeys = []
  const huddles = []
  flat.forEach((key) => {
    if (key === 'program_only') return
    const item = lookup[key] || {}
    const isHouse = item.type === 'houses' || item.house === true
    audienceKeys.push(`${isHouse ? 'house' : 'team'}:${key}`)
    huddles.push({ key, value: item.label || key, selected: true, show: true, house: isHouse, team: !isHouse })
  })
  return audienceKeys.length ? { audienceKeys, huddles } : { audienceKeys: ['all'], huddles: [] }
}

export const normalizeReel = (value) => {
  const base = createBlankReel((value && value.templateKey) || 'company_update')
  const next = { ...base, ...(value || {}) }
  const assetsById = assetLookup(next.assets)
  next.scenes = (next.scenes || []).map((item) => {
    const normalized = scene({ ...item, id: item.id || uid() })
    const asset = assetsById[sceneAssetKey(normalized)]
    const mediaUrl = assetPreviewUrl(asset) || normalized.mediaUrl
    return mediaUrl ? { ...normalized, mediaUrl } : normalized
  })
  next.uploadFocalPoint = { ...base.uploadFocalPoint, ...((value && value.uploadFocalPoint) || {}) }
  next.poster = { ...base.poster, ...((value && value.poster) || {}) }
  next.uploadEffectKey = getEffectPreset(next.uploadEffectKey).key
  next.showBranding = next.showBranding !== false
  next.audio = { ...base.audio, ...((value && value.audio) || {}) }
  next.audio.trackKey = getAudioTrackKey(value && value.audio && value.audio.trackKey, Boolean(next.audio.track))
  next.analytics = { ...base.analytics, ...((value && value.analytics) || {}) }
  return next
}

export const snapshotReel = (reel) => JSON.stringify({
  ...reel,
  uploadedVideo: reel.uploadedVideo ? { ...reel.uploadedVideo, file: undefined } : null,
  subtitles: reel.subtitles ? { ...reel.subtitles, file: undefined } : null,
  audio: reel.audio ? { ...reel.audio, track: reel.audio.track ? { ...reel.audio.track, file: undefined } : null } : null,
  scenes: (reel.scenes || []).map((item) => ({ ...item, file: undefined })),
})

const stripAssetBackedPreviewUrls = (reel) => {
  const next = { ...reel }
  next.scenes = (next.scenes || []).map((item) => (hasAssetRef(item) ? { ...item, mediaUrl: '' } : item))
  if (hasAssetRef(next.uploadedVideo)) next.uploadedVideo = { ...next.uploadedVideo, url: '' }
  if (next.audio && next.audio.track && hasAssetRef(next.audio.track)) {
    next.audio = { ...next.audio, track: { ...next.audio.track, url: '' } }
  }
  return next
}

// Media files are uploaded separately. Autosave JSON contains only durable
// references returned by the asset API, never browser-only File/blob values.
export const serializeReel = (reel) => stripAssetBackedPreviewUrls(JSON.parse(snapshotReel(reel), (key, value) => (
  typeof value === 'string' && value.indexOf('blob:') === 0 ? '' : value
)))
